/**
 * Dimensions: the overworld, the Nether and the End.
 *
 * The engine keeps one chunk height (WORLD_HEIGHT) for every dimension; a
 * dimension only changes how chunks are generated, how the sky/fog look and
 * how coordinates map between dimensions. Pure data + helpers, no DOM: safe to
 * import from workers and the node smoke tests.
 */

export type DimensionId = 'overworld' | 'nether' | 'end';

export const DIMENSIONS: readonly DimensionId[] = ['overworld', 'nether', 'end'];

export interface DimensionSettings {
  id: DimensionId;
  /** Display name (F3 / toasts). */
  name: string;
  /** Logical build height: blocks live in y = 0 .. height-1. */
  height: number;
  /** Bedrock floor at y = 0 (+ a ragged layer above). */
  bedrockFloor: boolean;
  /** Bedrock roof at y = height-1 (+ a ragged layer below). */
  bedrockRoof: boolean;
  /** Lava (nether) / water (overworld) sea surface. */
  seaLevel: number;
  /** The dimension has a sky: skylight, sun, moon, stars, clouds, weather. */
  hasSky: boolean;
  /** Vanilla `ambient_light`: brightness floor lerped into the light table. */
  ambientLight: number;
  /** Fog colour (sRGB hex) when the dimension has no sky. */
  fogColor: number;
  /** Fog start / end as fractions of the render distance (thick in the nether). */
  fogNear: number;
  fogFar: number;
  /** Horizontal blocks of this dimension per overworld block (nether 1/8). */
  coordinateScale: number;
  /** Radius (blocks) searched for an existing portal on arrival here. */
  portalSearchRadius: number;
}

export const OVERWORLD: DimensionSettings = {
  id: 'overworld', name: 'Overworld', height: 192, bedrockFloor: true, bedrockRoof: false,
  seaLevel: 63, hasSky: true, ambientLight: 0, fogColor: 0xc0d8ff, fogNear: 0.72, fogFar: 1,
  coordinateScale: 1, portalSearchRadius: 128,
};

/**
 * The Nether: 128 tall, bedrock floor and roof, lava sea at y = 31, no
 * skylight anywhere (vanilla has_skylight=false, ambient_light=0.1), dense
 * red-brown fog and no celestial bodies. 1 block here = 8 in the overworld.
 */
export const NETHER: DimensionSettings = {
  id: 'nether', name: 'The Nether', height: 128, bedrockFloor: true, bedrockRoof: true,
  seaLevel: 31, hasSky: false, ambientLight: 0.1, fogColor: 0x330808, fogNear: 0.05, fogFar: 0.55,
  coordinateScale: 1 / 8, portalSearchRadius: 16,
};

/**
 * The End: 128 tall, no bedrock floor (the void kills), no day/night, weather
 * or clouds. Vanilla coordinates are used unscaled (island top y 62, pillars
 * 76..103). Vanilla's forceBrightLightmap is approximated by a bright, cool
 * dim-ambient in render/dimensionEnv.ts. Portals do not scale coordinates.
 */
export const END: DimensionSettings = {
  id: 'end', name: 'The End', height: 128, bedrockFloor: false, bedrockRoof: false,
  seaLevel: 0, hasSky: false, ambientLight: 0, fogColor: 0x181318, fogNear: 0.72, fogFar: 1,
  coordinateScale: 1, portalSearchRadius: 0,
};

export function dimensionSettings(id: DimensionId): DimensionSettings {
  return id === 'nether' ? NETHER : id === 'end' ? END : OVERWORLD;
}

export function isDimensionId(v: unknown): v is DimensionId {
  return v === 'overworld' || v === 'nether' || v === 'end';
}

/** The other end of a nether portal: overworld <-> nether (the End has its own portals; it maps to the overworld). */
export function portalDestination(id: DimensionId): DimensionId {
  return id === 'overworld' ? 'nether' : 'overworld';
}

/**
 * Horizontal coordinate in `to` for a position in `from` (vanilla 8:1).
 * Vertical position is not scaled; callers clamp to the destination height.
 */
export function scaleCoord(v: number, from: DimensionId, to: DimensionId): number {
  if (from === to) return v;
  return (v * dimensionSettings(to).coordinateScale) / dimensionSettings(from).coordinateScale;
}

/** Salt mixed into the world seed for per-dimension noise (keeps dims independent). */
export function dimensionSeed(seed: number, id: DimensionId): number {
  return id === 'nether' ? (seed ^ 0x4e455448) | 0 : id === 'end' ? (seed ^ 0x454e44) | 0 : seed;
}
